import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../context/LanguageContext";

// Import project documentation images
import projectBg from "../../images/bg/project-bg.png";
import gifRound from "../../images/icon/b10c3e43e836d32554bf.gif";
import projectImg01 from "../../images/project/1.jpg";
import projectImg02 from "../../images/project/2.jpg";
import projectImg03 from "../../images/project/3.jpg";
import projectImg04 from "../../images/project/4.jpg";

// Arrow SVG icon
const ArrowSvg: React.FC = () => (
  <>
    {[...Array(2)].map((_, i) => (
      <svg
        key={i}
        width="28"
        height="28"
        viewBox="0 0 28 28"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect
          x="5.06592"
          y="19.9785"
          width="20.5712"
          height="2.61221"
          transform="rotate(-40.2798 5.06592 19.9785)"
          fill="white"
        />
        <rect
          x="7.97095"
          y="7.24463"
          width="2.61221"
          height="2.61221"
          transform="rotate(-40.2798 7.97095 7.24463)"
          fill="white"
        />
        <rect
          x="11.6523"
          y="7.54834"
          width="2.61221"
          height="2.61221"
          transform="rotate(-40.2798 11.6523 7.54834)"
          fill="white"
        />
        <rect
          x="15.334"
          y="7.85205"
          width="2.61221"
          height="2.61221"
          transform="rotate(-40.2798 15.334 7.85205)"
          fill="white"
        />
        <rect
          x="18.7119"
          y="11.8374"
          width="2.61221"
          height="2.61221"
          transform="rotate(-40.2798 18.7119 11.8374)"
          fill="white"
        />
        <rect
          x="18.4084"
          y="15.52"
          width="2.61221"
          height="2.61221"
          transform="rotate(-40.2798 18.4084 15.52)"
          fill="white"
        />
        <rect
          x="18.104"
          y="19.2012"
          width="2.61221"
          height="2.61221"
          transform="rotate(-40.2798 18.104 19.2012)"
          fill="white"
        />
      </svg>
    ))}
  </>
);

const projectImages = [projectImg01, projectImg02, projectImg03, projectImg04];

const ProjectSection: React.FC = () => {
  const { t } = useLanguage();

  useEffect(() => {
    // Set dynamic background image
    const bgElement = document.querySelector<HTMLElement>(".project.bg_img");
    if (bgElement) {
      bgElement.style.backgroundImage = `url(${projectBg})`;
    }
  }, []);

  const displayedProjects = t.projects.items.slice(0, 4);

  return (
    <section className="project bg_img pt-135 pb-150">
      <div className="container">
        <div className="sec-title custom-sec-title xb-sec-padding text-center">
          <span className="sub-title">{t.projects.sectionSub}</span>
          <h2 className="title">
            <span className="round-img">
              <img src={gifRound} alt="animation" />
            </span>{" "}
            {t.projects.title}
          </h2>
          <div className="xb-heading-btn d-inline">
            <Link className="thm-btn agency-btn" to="/project">
              <span className="text">{t.projects.viewAll}</span>
              <span className="arrow">
                <span className="arrow-icon">
                  <ArrowSvg />
                </span>
              </span>
            </Link>
          </div>
        </div>
      </div>

      <div className="container mxw-1800">
        <div className="xb-project-wrap">
          {/* Project items */}
          <div className="xb-project-inner">
            {displayedProjects.map((project, index) => {
              const docImg = projectImages[index] || projectImg01;
              const caseUrl = `/project-details/${project.slug}`;
              const isReversed = index % 2 === 1;

              return (
                <div
                  key={project.id}
                  className="xb-project-item pca-project-card-wrapper"
                >
                  <div className="pca-project-card-inner">
                    <div
                      className={`row g-4 g-lg-5 align-items-center w-100 m-0 ${
                        isReversed
                          ? "flex-column-reverse flex-lg-row-reverse"
                          : "flex-column-reverse flex-lg-row"
                      }`}
                    >
                      {/* Left/Content Column */}
                      <div className="col-lg-6 pca-project-col-content p-0 pe-lg-4 ps-lg-4">
                        <div className="pca-project-tag-wrap">
                          <span className="pca-project-tag-pill">
                            <span className="pca-pulse-dot"></span>
                            {project.industry}
                          </span>
                          {/* <span className="pca-project-client-pill">
                            {project.client} • {project.location}
                          </span> */}
                        </div>

                        <h2 className="pca-project-title">
                          <Link to={caseUrl}>{project.title}</Link>
                        </h2>

                        <p className="pca-project-desc">{project.description}</p>

                        {/* Measurable Impact Box */}
                        <div className="pca-project-impact-box">
                          <div className="pca-impact-title">{t.projects.impactLabel}</div>
                          <ul className="pca-impact-list">
                            {project.impact.map((imp, idx) => (
                              <li key={idx}>
                                <span className="pca-check-icon">✓</span> 
                                <span>{imp}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Technologies & Single CTA Button */}
                        <div className="pca-project-bottom-wrap">
                          <div className="pca-tech-stack-wrap">
                            <span className="pca-tech-label">{t.projects.technologiesLabel}</span>
                            <div className="pca-tech-badges">
                              {project.techs.map((tech, i) => (
                                <span key={i} className="pca-tech-badge">
                                  {tech}
                                </span>
                              ))}
                            </div>
                          </div>

                          <div className="pca-project-btn-wrap">
                            <Link className="thm-btn agency-btn" to={caseUrl}>
                              <span className="text">{t.projects.readMore}</span>
                              <span className="arrow">
                                <span className="arrow-icon">
                                  <ArrowSvg />
                                </span>
                              </span>
                            </Link>
                          </div>
                        </div>
                      </div>

                      {/* Right/Visual Mockup Column */}
                      <div className="col-lg-6 pca-project-col-visual p-0 pe-lg-3 ps-lg-3">
                        <Link
                          to={caseUrl}
                          className="pca-mockup-frame d-block"
                          title={`${project.title}`}
                        >
                          {/* Window Top Bar (Cyber Browser) */}
                          <div className="pca-mockup-header">
                            <div className="pca-mockup-dots">
                              <span className="pca-dot dot-red"></span>
                              <span className="pca-dot dot-yellow"></span>
                              <span className="pca-dot dot-green"></span>
                            </div>
                            <div className="pca-mockup-address">
                              <i className="fas fa-lock me-1" style={{ fontSize: "10px", color: "#00FF97" }}></i>
                              <span>system.primaciptaartifisial.co.id/{project.slug}</span>
                            </div>
                            <div className="pca-mockup-live-badge">
                              <span className="live-indicator"></span>
                              LIVE
                            </div>
                          </div>

                          {/* Crisp Project Documentation Image */}
                          <div className="pca-mockup-body">
                            <img
                              src={docImg}
                              alt={project.title}
                              className="pca-mockup-img"
                              loading="lazy"
                            />
                          </div>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectSection;
